/**
 * OpenAI Data Source - v13 Section 6.10 (Sprint 11g)
 *
 * Direct fetcher for OpenAI's model list.
 * These are the REAL model IDs that the OpenAI API accepts.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  NO HARDCODED MODEL DATA - All model info comes from OpenAI API              ║
 * ║                                                                              ║
 * ║  OpenAI API provides: model ID, created, owned_by                            ║
 * ║  OpenAI API does NOT provide: pricing, context window, max completion        ║
 * ║  These should be enriched from OpenRouter metadata                           ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see https://platform.openai.com/docs/api-reference/models
 */
/**
 * Create display name from model ID using generic cleanup
 * No hardcoded mapping - just transform the ID into readable format
 */
const createDisplayName = (modelId) => {
    return modelId
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
/**
 * Filter for chat/completion models (exclude embeddings, TTS, whisper, etc.)
 * This is TYPE-based filtering, not model-name filtering
 */
const isChatModel = (modelId) => {
    const id = modelId.toLowerCase();
    // Exclude non-chat model TYPES
    if (id.includes('embed'))
        return false;
    if (id.includes('whisper'))
        return false;
    if (id.includes('tts'))
        return false;
    if (id.includes('dall-e'))
        return false;
    if (id.includes('davinci-002'))
        return false; // Base completion model
    if (id.includes('babbage-002'))
        return false; // Base completion model
    // Include models that are chat/completion capable
    // GPT models, o1/o3 reasoning models are all chat-capable
    return true;
};
/**
 * Detect if model is a reasoning model (o1, o3 series)
 * Based on model TYPE indicator in the ID
 */
const isReasoningModel = (modelId) => {
    const id = modelId.toLowerCase();
    // o1 and o3 series are reasoning models
    if (id.startsWith('o1') || id.startsWith('o3')) {
        return true;
    }
    return null; // Unknown
};
/**
 * Fetch models directly from OpenAI API
 * Returns only API-provided data - no hardcoded pricing or limits
 *
 * @param apiKey - OpenAI API key
 * @returns Array of ModelMetadata for OpenAI models (with null for missing fields)
 */
export const fetchFromOpenAI = async (apiKey) => {
    const response = await fetch('https://api.openai.com/v1/models', {
        headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`OpenAI API returned ${response.status}: ${text || response.statusText}`);
    }
    const data = await response.json();
    if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from OpenAI API');
    }
    const models = [];
    for (const model of data.data) {
        // Skip non-chat models (type-based filtering)
        if (!isChatModel(model.id))
            continue;
        // Use only API-provided data - null for missing fields
        models.push({
            id: model.id,
            provider: 'openai',
            displayName: createDisplayName(model.id),
            contextWindow: null, // API doesn't provide this - enrich from OpenRouter
            maxCompletionTokens: null, // API doesn't provide this - enrich from OpenRouter
            pricing: null, // API doesn't provide pricing - enrich from OpenRouter
            capabilities: [], // API doesn't provide this
            zeroDataRetention: null, // Unknown for OpenAI
            isReasoningModel: isReasoningModel(model.id),
        });
    }
    return models;
};
